import { useEffect, useRef } from 'react'
import type { ReactNode } from 'react'
import './dialogo.css'

interface Props {
    titulo: string
    children: ReactNode
    alCerrar: () => void
    /** Acciones del pie. Se apilan en el telefono. */
    acciones?: ReactNode
    ancho?: 'chico' | 'medio' | 'ancho'
}

export function Dialogo({ titulo, children, alCerrar, acciones, ancho = 'medio' }: Props) {
    const panel = useRef<HTMLDivElement>(null)
    const focoPrevio = useRef<HTMLElement | null>(null)

    useEffect(() => {
        // Se recuerda donde estaba el foco para devolverlo al cerrar: en el POS
        // eso significa volver al buscador sin tocar el mouse.
        focoPrevio.current = document.activeElement as HTMLElement | null

        const primero = panel.current?.querySelector<HTMLElement>(
            'input:not([type="hidden"]), select, textarea, button',
        )

        primero?.focus()

        return () => focoPrevio.current?.focus()
    }, [])

    useEffect(() => {
        function alPulsar(evento: KeyboardEvent) {
            if (evento.key === 'Escape') {
                evento.stopPropagation()
                alCerrar()
                return
            }

            // Encierra el tabulador dentro del dialogo.
            if (evento.key !== 'Tab' || !panel.current) return

            const focalizables = panel.current.querySelectorAll<HTMLElement>(
                'input:not([type="hidden"]):not(:disabled), select:not(:disabled), textarea:not(:disabled), button:not(:disabled), [href]',
            )

            if (focalizables.length === 0) return

            const primero = focalizables[0]!
            const ultimo = focalizables[focalizables.length - 1]!

            if (evento.shiftKey && document.activeElement === primero) {
                evento.preventDefault()
                ultimo.focus()
            } else if (!evento.shiftKey && document.activeElement === ultimo) {
                evento.preventDefault()
                primero.focus()
            }
        }

        window.addEventListener('keydown', alPulsar, true)
        return () => window.removeEventListener('keydown', alPulsar, true)
    }, [alCerrar])

    return (
        <div className="capa-dialogo" onMouseDown={alCerrar} role="presentation">
            <div
                ref={panel}
                className={`dialogo dialogo-${ancho}`}
                role="dialog"
                aria-modal="true"
                aria-label={titulo}
                onMouseDown={(evento) => evento.stopPropagation()}
            >
                <header className="dialogo-cabecera">
                    <h2>{titulo}</h2>
                    <button
                        type="button"
                        className="boton boton-fantasma"
                        onClick={alCerrar}
                        aria-label="Cerrar"
                    >
                        ✕
                    </button>
                </header>

                <div className="dialogo-cuerpo">{children}</div>

                {acciones && <footer className="dialogo-pie">{acciones}</footer>}
            </div>
        </div>
    )
}
